import React, { useState, useEffect } from "react";
import {
  Stack,
  Button,
  Select,
  TextInput,
  ActionIcon,
  Group,
} from "@mantine/core";
import { DataTable } from "mantine-datatable";
import { IconTrash } from "@tabler/icons-react";
import { gql } from "@apollo/client";

import { useQuery } from "@apollo/client/react";

const ITEMS_AND_CURRENCIES_QUERY = gql`
  query {
    items(page: { first: 1000 }) {
      nodes {
        id
        name
      }
    }
    currencies(page: { first: 1000 }) {
      nodes {
        id
        name
        symbol
      }
    }
  }
`;

export const ShopItemsEditor = ({
  value = [],
  onChange,
}: {
  value?: Array<{ id: string; quantity: number; type: "ITEM" | "CURRENCY" }>;
  onChange: (
    items: Array<{ id: string; quantity: number; type: "ITEM" | "CURRENCY" }>,
  ) => void;
}) => {
  const [items, setItems] = useState(value);
  const { data } = useQuery<any>(ITEMS_AND_CURRENCIES_QUERY);

  useEffect(() => {
    onChange(items);
  }, [items]);

  const itemOptions =
    data?.items?.nodes?.map((item: any) => ({
      value: item.id,
      label: item.name,
    })) || [];

  const currencyOptions =
    data?.currencies?.nodes?.map((curr: any) => ({
      value: curr.id,
      label: `${curr.name} (${curr.symbol})`,
    })) || [];

  return (
    <Stack>
      <Button
        size="xs"
        onClick={() => {
          setItems([...items, { id: "", quantity: 1, type: "ITEM" }]);
        }}
      >
        Add Reward Item/Currency
      </Button>
      {items.length > 0 && (
        <DataTable
          records={items}
          columns={[
            {
              accessor: "type",
              title: "Type",
              render: (record, index) => (
                <Select
                  value={record.type}
                  data={[
                    { value: "ITEM", label: "Item" },
                    { value: "CURRENCY", label: "Currency" },
                  ]}
                  onChange={(value) => {
                    const newItems = [...items];
                    newItems[index].type = value as "ITEM" | "CURRENCY";
                    newItems[index].id = ""; // Reset selection when type changes
                    setItems(newItems);
                  }}
                />
              ),
            },
            {
              accessor: "id",
              title: "Item/Currency",
              render: (record, index) => (
                <Select
                  placeholder={`Select ${record.type === "ITEM" ? "item" : "currency"}`}
                  value={record.id}
                  data={record.type === "ITEM" ? itemOptions : currencyOptions}
                  onChange={(value) => {
                    const newItems = [...items];
                    newItems[index].id = value || "";
                    setItems(newItems);
                  }}
                  searchable
                />
              ),
            },
            {
              accessor: "quantity",
              title: "Quantity",
              render: (record, index) => (
                <TextInput
                  type="number"
                  value={record.quantity}
                  onChange={(e) => {
                    const newItems = [...items];
                    newItems[index].quantity = parseInt(e.target.value) || 1;
                    setItems(newItems);
                  }}
                  min={1}
                />
              ),
            },
            {
              accessor: "actions",
              title: "",
              render: (record, index) => (
                <ActionIcon
                  color="red"
                  onClick={() => {
                    const newItems = [...items];
                    newItems.splice(index, 1);
                    setItems(newItems);
                  }}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              ),
            },
          ]}
        />
      )}
    </Stack>
  );
};

export const CurrencyPricesEditor = ({
  value = [],
  onChange,
}: {
  value?: Array<{ id: string; quantity: number }>;
  onChange: (prices: Array<{ id: string; quantity: number }>) => void;
}) => {
  const [prices, setPrices] = useState(value);
  const { data } = useQuery<any>(gql`
    query {
      currencies(page: { first: 1000 }) {
        nodes {
          id
          name
          symbol
        }
      }
    }
  `);

  useEffect(() => {
    onChange(prices);
  }, [prices]);

  const currencyOptions =
    data?.currencies?.nodes?.map((curr: any) => ({
      value: curr.id,
      label: `${curr.name} (${curr.symbol})`,
    })) || [];

  return (
    <Stack>
      <Button
        size="xs"
        onClick={() => {
          setPrices([...prices, { id: "", quantity: 0 }]);
        }}
      >
        Add Currency Price
      </Button>
      {prices.length > 0 && (
        <DataTable
          records={prices}
          columns={[
            {
              accessor: "id",
              title: "Currency",
              render: (record, index) => (
                <Select
                  placeholder="Select currency"
                  value={record.id}
                  data={currencyOptions}
                  onChange={(value) => {
                    const newPrices = [...prices];
                    newPrices[index].id = value || "";
                    setPrices(newPrices);
                  }}
                  searchable
                />
              ),
            },
            {
              accessor: "quantity",
              title: "Amount",
              render: (record, index) => (
                <TextInput
                  type="number"
                  value={record.quantity}
                  onChange={(e) => {
                    const newPrices = [...prices];
                    newPrices[index].quantity = parseInt(e.target.value) || 0;
                    setPrices(newPrices);
                  }}
                  min={0}
                />
              ),
            },
            {
              accessor: "actions",
              title: "",
              render: (record, index) => (
                <ActionIcon
                  color="red"
                  onClick={() => {
                    const newPrices = [...prices];
                    newPrices.splice(index, 1);
                    setPrices(newPrices);
                  }}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              ),
            },
          ]}
        />
      )}
    </Stack>
  );
};
